import React, { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate, Navigate, useLocation } from "react-router-dom";
import TitanLogo from "@/components/TitanLogo";
import { useAuth } from "@/context/AuthContext";
import {
  LayoutDashboard,
  Car,
  ClipboardList,
  Sparkles,
  TrendingUp,
  MessageCircle,
  CreditCard,
  LogOut,
  Loader2,
  Menu,
  X,
} from "lucide-react";
import PaywallBanner from "@/components/PaywallBanner";

const ITEMS = [
  { to: "/admin", label: "Overview", icon: LayoutDashboard, end: true },
  { to: "/admin/cars", label: "Fleet", icon: Car },
  { to: "/admin/bookings", label: "Bookings", icon: ClipboardList },
  { to: "/admin/quotes", label: "Quotes", icon: Sparkles },
  { to: "/admin/leads", label: "Marketing", icon: TrendingUp },
  { to: "/admin/wingman", label: "Akua Titan", icon: MessageCircle },
  { to: "/admin/billing", label: "Billing", icon: CreditCard },
];

export default function AdminLayout() {
  const { user, loading, logout } = useAuth();
  const nav = useNavigate();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);

  // Close mobile menu on route change
  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  // Lock body scroll when mobile menu is open
  useEffect(() => {
    if (menuOpen) {
      document.body.style.overflow = "hidden";
      return () => {
        document.body.style.overflow = "";
      };
    }
  }, [menuOpen]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="animate-spin text-[#e63946]" />
      </div>
    );
  }
  if (!user || user.role !== "admin") {
    return <Navigate to="/admin/login" replace />;
  }

  const activeLabel = ITEMS.find((it) => {
    if (it.end) return location.pathname === it.to;
    return location.pathname === it.to || location.pathname.startsWith(`${it.to}/`);
  })?.label || "Admin";

  const SidebarBody = ({ onNavigate, idPrefix = "" }) => (
    <>
      <nav className="flex-1 p-3 space-y-1 overflow-y-auto" data-testid={`${idPrefix}admin-sidebar-nav`}>
        {ITEMS.map((it) => (
          <NavLink
            key={it.to}
            to={it.to}
            end={it.end}
            onClick={onNavigate}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-3 text-sm uppercase font-bold tracking-wider transition-colors ${
                isActive ? "bg-[#e63946] text-white" : "text-white/70 hover:text-white hover:bg-white/5"
              }`
            }
            data-testid={`${idPrefix}admin-nav-${it.label.toLowerCase().replace(/\s/g, "-")}`}
          >
            <it.icon size={16} /> {it.label}
          </NavLink>
        ))}
      </nav>
      <div className="p-3 border-t border-white/10">
        <button
          onClick={async () => {
            await logout();
            nav("/admin/login");
          }}
          className="flex items-center gap-3 px-3 py-3 text-sm uppercase font-bold tracking-wider text-white/70 hover:text-white hover:bg-white/5 w-full"
          data-testid={`${idPrefix}admin-logout-btn`}
        >
          <LogOut size={16} /> Sign out
        </button>
        <div className="text-[0.65rem] text-white/50 mt-3 px-3 break-all">
          Signed in as<br />
          <span className="text-white">{user.email}</span>
        </div>
      </div>
    </>
  );

  return (
    <div className="min-h-screen bg-[#f7f8fa] lg:flex" data-testid="admin-layout">
      {/* Desktop sidebar (lg+) */}
      <aside
        className="hidden lg:flex w-64 bg-[#0d2040] text-white flex-col"
        data-testid="admin-sidebar-desktop"
      >
        <div className="p-5 border-b border-white/10">
          <TitanLogo variant="light" size="sm" />
        </div>
        <SidebarBody idPrefix="desktop-" />
      </aside>

      {/* Mobile top bar (< lg) */}
      <header
        className="lg:hidden sticky top-0 z-30 bg-[#0d2040] text-white flex items-center justify-between px-4 py-3 shadow-md"
        data-testid="admin-mobile-topbar"
      >
        <button
          onClick={() => setMenuOpen(true)}
          className="p-2 -ml-2 hover:bg-white/10"
          aria-label="Open menu"
          data-testid="admin-mobile-menu-open"
        >
          <Menu size={22} />
        </button>
        <div className="font-display text-sm font-bold uppercase tracking-widest">
          {activeLabel}
        </div>
        <TitanLogo variant="light" size="sm" />
      </header>

      {/* Mobile slide-in drawer */}
      {menuOpen && (
        <>
          <button
            type="button"
            className="lg:hidden fixed inset-0 z-40 bg-black/60"
            onClick={() => setMenuOpen(false)}
            aria-label="Close menu"
            data-testid="admin-mobile-menu-backdrop"
          />
          <aside
            className="lg:hidden fixed top-0 left-0 bottom-0 z-50 w-72 max-w-[85vw] bg-[#0d2040] text-white flex flex-col shadow-2xl"
            data-testid="admin-mobile-drawer"
          >
            <div className="p-5 border-b border-white/10 flex items-center justify-between">
              <TitanLogo variant="light" size="sm" />
              <button
                onClick={() => setMenuOpen(false)}
                className="p-2 -mr-2 hover:bg-white/10"
                aria-label="Close menu"
                data-testid="admin-mobile-menu-close"
              >
                <X size={20} />
              </button>
            </div>
            <SidebarBody onNavigate={() => setMenuOpen(false)} idPrefix="mobile-" />
          </aside>
        </>
      )}

      <main className="flex-1 min-w-0 overflow-x-auto" data-testid="admin-main">
        <PaywallBanner />
        <Outlet />
      </main>
    </div>
  );
}
